import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import vert from "./shaders/drone.vert.glsl";
import frag from "./shaders/drone.frag.glsl";
import { assign, type Cloud } from "./formations";
import { audioBus } from "./audioBus";
import { DEFAULT_FX, type AudioFX } from "./audioFx";
import { PLATE_L } from "./source";

/** Live standing-wave drive for a cymatics plate (null = off). */
export type CymDrive = { m: number; n: number; mix: number; amp: number; speed: number };

type Props = {
  /** Target point cloud (positions + colors) — a shape, text, SVG or image. */
  cloud: Cloud;
  brightness?: number;
  size?: number;
  morphDuration?: number;
  cym?: CymDrive | null;
  /** Size: scales the target shape/swarm space (drones spread into more room). */
  scale?: number;
  /** The audio-FX rack (which music effects are on, and how hard). */
  fx?: AudioFX;
};

const STAGGER = 0.4;

/**
 * The drone swarm. Holds a fixed population and animates each transition on the
 * GPU with staggered easing. The morph is triggered by the identity of the
 * `cloud` object itself, so it always flies to the cloud currently on screen —
 * never one step behind. On every retarget it samples where each drone actually
 * is (respecting an in-flight morph) so switches never snap.
 */
export function DroneField({ cloud, brightness = 1.5, size = 6.0, morphDuration = 2.6, cym = null, scale = 1, fx = DEFAULT_FX }: Props) {
  const matRef = useRef<THREE.ShaderMaterial>(null);
  const ptsRef = useRef<THREE.Points>(null);
  const mountCloud = useRef(cloud);
  const count = cloud.positions.length / 3;
  const cymRef = useRef(cym);
  cymRef.current = cym;
  const curCym = useRef<{ m: number; n: number } | null>(null);
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const hueAngle = useRef(0);
  const scaleTarget = useRef(scale);
  scaleTarget.current = scale;
  const curScale = useRef(scale);

  const geometry = useMemo(() => {
    const c = mountCloud.current;
    const g = new THREE.BufferGeometry();
    const n = c.positions.length / 3;
    const seed = new Float32Array(n);
    for (let i = 0; i < n; i++) seed[i] = Math.random();
    g.setAttribute("position", new THREE.BufferAttribute(c.positions.slice(), 3));
    g.setAttribute("aTarget", new THREE.BufferAttribute(c.positions.slice(), 3));
    g.setAttribute("aColorA", new THREE.BufferAttribute(c.colors.slice(), 3));
    g.setAttribute("aColorB", new THREE.BufferAttribute(c.colors.slice(), 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const uniforms = useMemo(
    () => ({
      uMorph: { value: 1 },
      uTime: { value: 0 },
      uSize: { value: size },
      uBrightness: { value: brightness },
      uStagger: { value: STAGGER },
      uTransKind: { value: 0 }, // free-play never uses transition effects
      uTransAmt: { value: 0 },
      uAudioLevel: { value: 0 },
      uAudioBeat: { value: 0 },
      uShimmer: { value: 0 },
      uHueShift: { value: 0 },
      uCymAmp: { value: 0 },
      uCymM: { value: 0 },
      uCymN: { value: 0 },
      uCymMix: { value: -1 },
      uCymL: { value: PLATE_L },
      uCymSpeed: { value: 2.4 },
      uScale: { value: scale },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.uniforms.uSize.value = size;
    matRef.current.uniforms.uBrightness.value = brightness;
  }, [size, brightness]);

  const lastCloud = useRef(cloud);
  const morph = useRef({ active: false, t: 1 });

  // Morph whenever the cloud DATA changes (not a separate key) — this is the
  // fix for the "one step behind" bug: we always target the current cloud.
  useEffect(() => {
    if (cloud === lastCloud.current) return;
    lastCloud.current = cloud;

    const g = geometry;
    const start = g.getAttribute("position") as THREE.BufferAttribute;
    const target = g.getAttribute("aTarget") as THREE.BufferAttribute;
    const colA = g.getAttribute("aColorA") as THREE.BufferAttribute;
    const colB = g.getAttribute("aColorB") as THREE.BufferAttribute;
    const seed = g.getAttribute("aSeed") as THREE.BufferAttribute;
    const sa = start.array as Float32Array;
    const ta = target.array as Float32Array;
    const caa = colA.array as Float32Array;
    const cba = colB.array as Float32Array;
    const sda = seed.array as Float32Array;

    // freeze the swarm where it currently renders (mid-morph safe)
    const uMorph = matRef.current?.uniforms.uMorph.value ?? 1;
    const span = Math.max(1 - STAGGER, 1e-4);
    for (let i = 0; i < count; i++) {
      const local = Math.min(Math.max((uMorph - sda[i] * STAGGER) / span, 0), 1);
      const e = local * local * local * (local * (local * 6 - 15) + 10);
      for (let k = 0; k < 3; k++) {
        const j = i * 3 + k;
        sa[j] = sa[j] + (ta[j] - sa[j]) * e;
        caa[j] = caa[j] + (cba[j] - caa[j]) * e;
      }
    }

    const assigned = assign(sa, cloud.positions, cloud.colors, count);
    ta.set(assigned.positions);
    cba.set(assigned.colors);
    start.needsUpdate = true;
    target.needsUpdate = true;
    colA.needsUpdate = true;
    colB.needsUpdate = true;

    morph.current.active = true;
    morph.current.t = 0;
    if (matRef.current) matRef.current.uniforms.uMorph.value = 0;
  }, [cloud, geometry, count]);

  useFrame((_, dt) => {
    const m = matRef.current;
    if (!m) return;
    m.uniforms.uTime.value += dt;

    // ---- the audio-FX rack (each unit gated + scaled by its dials) ----
    const av = audioBus.values;
    const fxc = fxRef.current;
    m.uniforms.uAudioLevel.value = fxc.pulse.on ? av.level * fxc.pulse.amt : 0;
    m.uniforms.uAudioBeat.value = fxc.pulse.on ? av.beat * fxc.pulse.amt : 0;
    m.uniforms.uShimmer.value = fxc.shimmer.on ? av.treble * fxc.shimmer.amt : 0;
    if (fxc.hue.on) hueAngle.current += av.mid * fxc.hue.amt * dt * 0.6;
    m.uniforms.uHueShift.value = fxc.hue.on ? hueAngle.current : 0;
    // bass torques the whole swarm (a render transform — physics untouched)
    if (fxc.spin.on && ptsRef.current) ptsRef.current.rotation.y += av.bass * av.bass * 1.2 * fxc.spin.amt * dt;

    const cy = cymRef.current;
    // the plate pumps harder on the music — antinodes surge on the drops
    m.uniforms.uCymAmp.value = cy ? cy.amp * (1 + av.bass * 1.6 + av.beat * 0.5) : 0;
    // Size eases toward its target; the bass swell breathes on top
    curScale.current += (scaleTarget.current - curScale.current) * Math.min(1, dt * 8);
    const swell = fxc.swell.on ? (av.bass * 0.12 + av.beat * 0.05) * fxc.swell.amt : 0;
    m.uniforms.uScale.value = curScale.current * (1 + swell);
    if (cy) {
      // mode numbers EASE toward the target: a sweep melts figure into figure
      // (non-integer plates are valid Chladni fields, they morph beautifully)
      if (!curCym.current) curCym.current = { m: cy.m, n: cy.n }; // snap on entry
      const ck = Math.min(1, dt * 2.5);
      curCym.current.m += (cy.m - curCym.current.m) * ck;
      curCym.current.n += (cy.n - curCym.current.n) * ck;
      m.uniforms.uCymM.value = curCym.current.m;
      m.uniforms.uCymN.value = curCym.current.n;
      m.uniforms.uCymMix.value = cy.mix;
      m.uniforms.uCymSpeed.value = cy.speed;
    } else {
      curCym.current = null;
    }
    if (morph.current.active) {
      morph.current.t = Math.min(morph.current.t + dt / morphDuration, 1);
      m.uniforms.uMorph.value = morph.current.t;
      if (morph.current.t >= 1) morph.current.active = false;
    }
  });

  return (
    <points ref={ptsRef} name="swarm" geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={matRef}
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
